import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineEye, HiOutlineX, HiOutlineSearch } from 'react-icons/hi';
import axios from 'axios';
import Separador from '../componentes/Separador NavBar/Separador';
import editorJsToHtml from '../utils/editorJsToHtml';
import './estilos/BlogPost.css';

const BlogPost = () => {
    const { slug } = useParams();
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lightboxImage, setLightboxImage] = useState(null);

    const lang = i18n.language === 'es' ? 'es' : 'en';
    const API_URL = process.env.REACT_APP_CMS_API_URL || 'http://localhost:5001/api';
    const MEDIA_URL = (process.env.REACT_APP_CMS_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

    useEffect(() => {
        fetchPost();
    }, [slug, lang]);

    const fetchPost = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Verificar si ya se vio este artículo en esta sesión
            let viewedPosts = [];
            try {
                viewedPosts = JSON.parse(sessionStorage.getItem('viewedPosts') || '[]');
            } catch (e) {
                viewedPosts = [];
            }
            
            const alreadyViewed = viewedPosts.includes(slug);
            
            const response = await axios.get(`${API_URL}/blog/slug/${slug}`, {
                params: { 
                    lang,
                    incrementViews: alreadyViewed ? 'false' : 'true'
                }
            });
            
            setPost(response.data);
            
            // Marcar este artículo como visto en esta sesión
            if (!alreadyViewed) {
                viewedPosts.push(slug);
                sessionStorage.setItem('viewedPosts', JSON.stringify(viewedPosts));
            }
        } catch (error) {
            console.error('Error al cargar el post:', error);
            setError('No se pudo cargar el artículo');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString(lang, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const openLightbox = (imageUrl) => {
        setLightboxImage(imageUrl);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setLightboxImage(null);
        document.body.style.overflow = 'auto';
    };

    // Cerrar lightbox con tecla Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                closeLightbox();
            }
        };
        
        if (lightboxImage) {
            window.addEventListener('keydown', handleEscape);
            return () => window.removeEventListener('keydown', handleEscape);
        }
    }, [lightboxImage]);

    if (loading) {
        return (
            <div className="blog-post-page">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>{t('blog.loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="blog-post-page">
                <div className="error-container">
                    <h2>{t('blog.notFound')}</h2>
                    <p>{error || t('blog.notFoundDesc')}</p>
                    <Link to={`/${lang}/blog`} className="btn-back">
                        ← {t('blog.backToBlog')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-post-page">
            <Separador />
            <div className="blog-post-container">
                <Link to={`/${lang}/blog`} className="back-link">
                    ← {t('blog.backToBlog')}
                </Link>

                <article className="blog-post">
                    {post.featured_image && (
                        <div className="post-featured-image" onClick={() => openLightbox(`${MEDIA_URL}${post.featured_image}`)}>
                            <img src={`${MEDIA_URL}${post.featured_image}`} alt={post[`title_${lang}`]} />
                            <span className="image-zoom-overlay">
                                <HiOutlineSearch />
                            </span>
                        </div>
                    )}

                    <header className="post-header">
                        <div className="post-meta-info">
                            {post.category_name && (
                                <span className="post-category-badge">{post.category_name}</span>
                            )}
                            <time className="post-date">{formatDate(post.published_at)}</time>
                        </div>
                        <h1 className="post-title-main">{post[`title_${lang}`]}</h1>
                        <div className="post-author-info">
                            <span className="author-by">
                                {t('blog.by')} {post.author_name === 'ADMINISTRADOR' 
                                    ? t('blog.administrator')
                                    : post.author_name}
                            </span>
                            <span className="post-views">
                                <HiOutlineEye /> {post.views || 0} {(post.views || 0) === 1 ? t('blog.view') : t('blog.views')}
                            </span>
                        </div>
                    </header>

                    <div 
                        className="post-content" 
                        dangerouslySetInnerHTML={{ __html: editorJsToHtml(post[`content_${lang}`]) }}
                    />

                    {post.gallery_images && post.gallery_images.length > 0 && (
                        <div className="post-gallery">
                            <h3>{t('blog.imageGallery')}</h3>
                            <div className="gallery-grid">
                                {post.gallery_images.map((img, index) => (
                                    <div 
                                        key={index} 
                                        className="gallery-item"
                                        onClick={() => openLightbox(`${MEDIA_URL}${img}`)}
                                    >
                                        <img src={`${MEDIA_URL}${img}`} alt={`${t('blog.gallery')} ${index + 1}`} />
                                        <span className="image-zoom-overlay">
                                            <HiOutlineSearch />
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </article>
            </div>

            {/* Lightbox Modal */}
            {lightboxImage && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>
                        <HiOutlineX />
                    </button>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <img src={lightboxImage} alt={t('blog.imageGallery')} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogPost;
